import { useSelector, useDispatch } from "react-redux";
import { Scrollbar } from 'react-scrollbars-custom';

import ScrollBoxComponent from '../../ScrollBox/ScrollBox';
import TicketListItem from './TicketListItem/TicketListItem';
import "./TicketList.scss";
import { useParams } from 'react-router-dom';
import { fetchAllTickets, setSelectedTicket } from "../../../slices/TicketSlice";
import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";

/**
 * TicketList Component
 *
 * @component
 * @description
 * Displays a scrollable list of tickets for a given user context. On initial render,
 * if the tickets slice status is "idle", it dispatches 'fetchAllTickets()' to load data.
 * Shows loading and error states, and renders each ticket through 'TicketListItem' when succeeded.
 *
 * @param {Object} props - Component props.
 * @param {string} [props.width='100%'] - Width of the scroll container.
 * @param {'student'|'staff'|'admin'} props.user - Which user context the list is presented for.
 *
 * @returns {JSX.Element} A scrollable ticket list wrapped in 'ScrollBoxComponent' and 'Scrollbar'.
 *
 * @example
 * // Render a student-focused list in a fixed-width panel:
 * <TicketList width="300px" user="student" />
 */



export default function TicketList({ width, user }) {
  const tickets = useSelector((state) => state.tickets.ticketsList);
  const status = useSelector((state) => state.tickets.ticketStatus);
  const { ticketId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  useEffect(() => {
  if (ticketId && tickets.length === 0 && status !== "loading") {
    dispatch(fetchAllTickets());
  }
}, [ticketId, tickets.length, status, dispatch]);

  useEffect(() => {
  if (status === "succeeded" && ticketId && tickets.length > 0) {
    const foundTicket = tickets.find(t => String(t.id) === String(ticketId));
    if (foundTicket) {
      dispatch(setSelectedTicket(foundTicket));
    } else {
      console.warn("Ticket not found in list, redirecting...");
      navigate("/", { replace: true });
    }
  }
}, [status, ticketId, tickets, dispatch, navigate]);

  return (
    <div>
      <div className="my-tickets-label">My Tickets</div>
      <ScrollBoxComponent widthbox={width} user={user} className="ScrollBoxComponent" style={{ minHeight: '500px', display: 'block'}}>

        {status === "succeeded" && (
          <Scrollbar
            className="scrollbar"
            trackYProps={{ 
              style: { width: 8 },
            }}
            noScrollX={true}
          >
            {tickets && tickets.length === 0 ? (
                <div className="no-tickets" data-testid="no-tickets-message">No tickets...</div>
              ) : (
                <div className="ticket-list" data-testid="ticket-list-items">
                  {tickets
                    ?.slice()
                    .sort((a, b) => { // negative -> A before B, positive -> B before A, 0 -> no change

                      // Status CLOSED should be at end of list
                      const isAClosed = a.status?.toLowerCase() === 'closed';
                      const isBClosed = b.status?.toLowerCase() === 'closed';

                      if (isAClosed && !isBClosed) return 1;  // B to the front, A to the end
                      if (!isAClosed && isBClosed) return -1; // Keep B at the end, A to the front

                      // If two CLOSED tickets or any other tickets, sort by createdAt (most recent first)
                      const dateA = new Date(a.createdAt).getTime();
                      const dateB = new Date(b.createdAt).getTime();
 
                      return dateB - dateA; // Most recent on top
                    })
                    .map((ticket) => (
                      <TicketListItem key={ticket.id} ticket={ticket} data-testid={`ticket-list-item-${ticket.id}`}/>
                  ))}
                  </div>
              )}
          </Scrollbar>
        )}
      </ScrollBoxComponent>
    </div>
  );
}